
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, ArrowRight, Sparkles, Calculator, FileText, Users, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Command {
  id: string;
  title: string;
  description: string;
  href: string;
  icon: React.ElementType;
  category: string;
  keywords: string[];
}

const commands: Command[] = [
  {
    id: 'ai-builder',
    title: 'AI Website Builder',
    description: 'Build your custom website with AI',
    href: '/website-wizard',
    icon: Sparkles,
    category: 'Tools',
    keywords: ['build', 'wizard', 'website', 'configure', 'ai']
  },
  {
    id: 'pricing',
    title: 'View Pricing',
    description: 'See our transparent pricing plans',
    href: '/pricing',
    icon: Calculator,
    category: 'Information',
    keywords: ['price', 'cost', 'plans', 'billing']
  },
  {
    id: 'products',
    title: 'Browse Products',
    description: 'Explore our 16 product modules',
    href: '/products',
    icon: FileText,
    category: 'Products',
    keywords: ['products', 'modules', 'features', 'crm', 'ai']
  },
  {
    id: 'about',
    title: 'About DIALGIA',
    description: 'Learn about our company and mission',
    href: '/about',
    icon: Users,
    category: 'Company',
    keywords: ['about', 'company', 'team', 'mission']
  },
  {
    id: 'portfolio',
    title: 'View Portfolio',
    description: 'See our case studies and success stories',
    href: '/portfolio',
    icon: BarChart3,
    category: 'Social Proof',
    keywords: ['portfolio', 'cases', 'success', 'clients']
  }
];

export const CommandPalette = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  const filteredCommands = commands.filter(command =>
    command.title.toLowerCase().includes(query.toLowerCase()) ||
    command.description.toLowerCase().includes(query.toLowerCase()) ||
    command.keywords.some(keyword => keyword.toLowerCase().includes(query.toLowerCase()))
  );

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredCommands[selectedIndex]) {
            navigate(filteredCommands[selectedIndex].href);
            onClose();
          }
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredCommands, navigate, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="fixed top-20 left-1/2 transform -translate-x-1/2 w-full max-w-2xl mx-auto p-4">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-scale-in">
          {/* Search Header */}
          <div className="flex items-center px-6 py-4 border-b border-gray-200">
            <Search className="h-5 w-5 text-muted-foreground mr-3" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search anything..."
              className="flex-1 text-lg border-0 focus:ring-0 p-0"
              autoFocus
            />
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">↵</kbd>
              <span className="text-xs">to select</span>
            </div>
          </div>

          {/* Commands List */}
          <div className="max-h-80 overflow-y-auto">
            {filteredCommands.length > 0 ? (
              <div className="py-2">
                {filteredCommands.map((command, index) => (
                  <button
                    key={command.id}
                    className={`w-full flex items-center px-6 py-3 hover:bg-gray-50 transition-colors text-left ${
                      index === selectedIndex ? 'bg-blue-50 border-r-2 border-blue-600' : ''
                    }`}
                    onClick={() => {
                      navigate(command.href);
                      onClose();
                    }}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 ${
                      index === selectedIndex 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <command.icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900">{command.title}</span>
                        <Badge variant="outline" className="text-xs">
                          {command.category}
                        </Badge>
                      </div>
                      <span className="text-sm text-gray-600">{command.description}</span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground ml-4" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-gray-500 mb-2">No results found</p>
                <p className="text-sm text-muted-foreground">Try searching for products, pricing, or features</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <kbd className="px-2 py-1 bg-white rounded">↑↓</kbd>
                  <span>navigate</span>
                </div>
                <div className="flex items-center space-x-1">
                  <kbd className="px-2 py-1 bg-white rounded">⎋</kbd>
                  <span>close</span>
                </div>
              </div>
              <div className="flex items-center space-x-1 text-blue-600">
                <Sparkles className="h-3 w-3" />
                <span>Powered by DIALGIA AI</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div 
        className="absolute inset-0"
        onClick={onClose}
      />
    </div>
  );
};
